import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import InfoPopup from './InfoPopup';
import * as FiIcons from 'react-icons/fi';

const { FiDollarSign, FiPlus, FiCopy, FiCheck, FiRefreshCw, FiHelpCircle } = FiIcons;

const GstInputCard = ({ label, value, onChange, field, icon, iconBg, iconColor, delay, darkMode }) => {
  const [copied, setCopied] = useState(false);

  const handleChange = (e) => {
    const rawVal = e.target.value;
    // Strictly numeric + one decimal point
    const sanitizedVal = rawVal.replace(/[^\d.]/g, '');
    const parts = sanitizedVal.split('.');
    const finalVal = parts.length > 2 ? `${parts[0]}.${parts.slice(1).join('')}` : sanitizedVal;

    if (finalVal === '' || /^\d*\.?\d*$/.test(finalVal)) {
      onChange(field, finalVal);
    }
  };

  const copyToClipboard = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, delay }}
      className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600 transition-colors duration-300"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <div className={`p-2 rounded ${iconBg} dark:opacity-90`}>
            <SafeIcon icon={icon} className={`text-lg ${iconColor}`} />
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-300 font-medium uppercase tracking-wider">{label}</p>
        </div>
        <button
          onClick={copyToClipboard}
          className={`p-1.5 rounded-md transition-all ${copied ? 'text-green-500' : 'text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
          title="Copy to clipboard"
        >
          <SafeIcon icon={copied ? FiCheck : FiCopy} className="text-xs" />
        </button>
      </div>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <span className="text-gray-500 dark:text-gray-400 text-sm">$</span>
        </div>
        <input
          type="text"
          inputMode="decimal"
          value={value}
          onChange={handleChange}
          placeholder="0.00"
          className="w-full pl-6 pr-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded shadow-sm focus:ring-1 focus:ring-blue-500 transition-colors font-bold text-xl text-gray-800 dark:text-white"
        />
      </div>
    </motion.div>
  );
};

const Divider = ({ symbol }) => (
  <div className="relative h-8 flex items-center justify-center -my-1 z-0">
    <div className="absolute w-full h-[1px] bg-gray-200 dark:bg-gray-700 top-1/2 transform -translate-y-1/2"></div>
    <div className="relative z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full w-6 h-6 flex items-center justify-center shadow-sm">
      {symbol === '+' ? <SafeIcon icon={FiPlus} className="text-xs text-gray-400" /> : <span className="text-gray-400 font-bold text-sm leading-none pb-0.5">=</span>}
    </div>
  </div>
);

const GstCalculator = ({ darkMode, values, setValues }) => {
  const [popupType, setPopupType] = useState(null);
  const GST_RATE = 0.10;

  const handleInputChange = useCallback((field, value) => {
    if (value === '') {
      setValues({ base: '', gst: '', total: '' });
      return;
    }
    const num = parseFloat(value);
    let newValues = { ...values, [field]: value };
    if (!isNaN(num)) {
      if (field === 'base') {
        newValues.gst = (num * GST_RATE).toFixed(2);
        newValues.total = (num * (1 + GST_RATE)).toFixed(2);
      } else if (field === 'gst') {
        newValues.base = (num / GST_RATE).toFixed(2);
        newValues.total = (num + (num / GST_RATE)).toFixed(2);
      } else if (field === 'total') {
        newValues.base = (num / (1 + GST_RATE)).toFixed(2);
        newValues.gst = (num - (num / (1 + GST_RATE))).toFixed(2);
      }
    }
    setValues(newValues);
  }, [values, setValues]);

  return (
    <>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-lg">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-4 transition-colors duration-300">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-bold text-gray-800 dark:text-white">GST Calculator (10%)</h2>
            <button onClick={() => setValues({ base: '', gst: '', total: '' })} className="flex items-center space-x-1 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800">
              <SafeIcon icon={FiRefreshCw} /> <span>Reset</span>
            </button>
          </div>
          <div className="space-y-0">
            <GstInputCard
              label="Base Price"
              field="base"
              value={values.base}
              onChange={handleInputChange}
              icon={FiDollarSign}
              iconBg="bg-blue-100 dark:bg-blue-900/30"
              iconColor="text-blue-600 dark:text-blue-400"
              delay={0.1}
              darkMode={darkMode}
            />
            <Divider symbol="+" />
            <GstInputCard
              label="GST Amount"
              field="gst"
              value={values.gst}
              onChange={handleInputChange}
              icon={FiDollarSign}
              iconBg="bg-orange-100 dark:bg-orange-900/30"
              iconColor="text-orange-600 dark:text-orange-400"
              delay={0.2}
              darkMode={darkMode}
            />
            <Divider symbol="=" />
            <GstInputCard
              label="Total Price"
              field="total"
              value={values.total}
              onChange={handleInputChange}
              icon={FiDollarSign}
              iconBg="bg-green-100 dark:bg-green-900/30"
              iconColor="text-green-600 dark:text-green-400"
              delay={0.3}
              darkMode={darkMode}
            />
          </div>
          <div className="mt-6 pt-3 border-t border-gray-200 dark:border-gray-700 space-y-2">
            <button onClick={() => setPopupType('gst-calculation')} className="w-full flex items-center justify-center space-x-1 px-3 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors duration-200 font-medium text-sm">
              <SafeIcon icon={FiHelpCircle} /> <span>How It's Calculated</span>
            </button>
            <button onClick={() => setPopupType('gst-disclaimer')} className="w-full flex items-center justify-center space-x-1 px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200 font-medium text-sm">
              <SafeIcon icon={FiHelpCircle} /> <span>Disclaimer</span>
            </button>
          </div>
        </div>
      </motion.div>
      <InfoPopup isOpen={popupType !== null} onClose={() => setPopupType(null)} type={popupType} darkMode={darkMode} />
    </>
  );
};

export default GstCalculator;